import { useState, useRef, useCallback, useEffect } from 'react';
import Modal from '../Common/Modal';

export default function AvatarCropper({ open, imageSrc, onCrop, onClose }) {
  const canvasRef = useRef();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);

  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setOffset({ x: 0, y: 0 });
      setScale(1);
      drawCanvas(img, { x: 0, y: 0 }, 1);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  const drawCanvas = useCallback((img, pos, s) => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    const size = 280;
    canvas.width = size;
    canvas.height = size;

    ctx.fillStyle = 'var(--bg-primary, #f5f5f5)';
    ctx.fillRect(0, 0, size, size);

    const ratio = Math.max(size / img.width, size / img.height) * s;
    const w = img.width * ratio;
    const h = img.height * ratio;
    const x = (size - w) / 2 + pos.x;
    const y = (size - h) / 2 + pos.y;

    ctx.drawImage(img, x, y, w, h);

    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2);
    ctx.stroke();
  }, []);

  useEffect(() => {
    if (imgRef.current) drawCanvas(imgRef.current, offset, scale);
  }, [offset, scale, drawCanvas]);

  const handleMouseDown = (e) => {
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setDragging(false);

  const handleWheel = (e) => {
    e.preventDefault();
    setScale((s) => Math.max(0.5, Math.min(3, s + (e.deltaY > 0 ? -0.1 : 0.1))));
  };

  const handleCrop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = 200;
    cropCanvas.height = 200;
    const ctx = cropCanvas.getContext('2d');

    ctx.beginPath();
    ctx.arc(100, 100, 100, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, 200, 200);

    const dataUrl = cropCanvas.toDataURL('image/jpeg', 0.9);
    onCrop(dataUrl);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="裁剪头像">
      <div className="avatar-cropper">
        <div
          className="crop-area"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <canvas ref={canvasRef} />
        </div>
        <div className="crop-controls">
          <label>缩放</label>
          <input
            type="range"
            min="0.5"
            max="3"
            step="0.1"
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
          />
        </div>
        <div className="crop-actions">
          <button className="btn-secondary" onClick={onClose}>取消</button>
          <button className="btn-primary" onClick={handleCrop}>确认裁剪</button>
        </div>
      </div>
    </Modal>
  );
}
